/**
 * DocSectionView - the inline section preview for the SOW section-type tabs.
 *
 * Fetches one section of the canonical .docx as structured contract children and
 * renders them as clean, normalized HTML (DocContractView) - NOT a faithful docx
 * render. A "View full document" footer opens the faithful modal (FilePreviewDialog)
 * for the whole file. Falls back to the supplied text excerpt when the asset is a
 * PDF, the heading did not match, or the fetch fails.
 */

import { useDocSection } from '../../../hooks/useDocSection';
import { DocContractView } from '../../../components/doc/DocContractView';

export interface DocSectionViewProps {
  /** Canonical document asset to clip the section from. */
  assetUuid: string | null;
  /** Lazy gate: only fetch + render when true (the visible tab). */
  active: boolean;
  /** Section heading to locate within the document. */
  heading?: string;
  /** Plain-text fallback shown when the structured section is unavailable. */
  fallbackText?: string;
  /** File name shown in the full-document modal title. */
  fileName?: string;
  /** Opens the faithful full-document modal scrolled to this section. */
  onViewFull?: () => void;
  disabled?: boolean;
}

export function DocSectionView({
  assetUuid,
  active,
  heading,
  fallbackText,
  onViewFull,
  disabled,
}: DocSectionViewProps) {
  const { children, found, loading } = useDocSection(assetUuid, heading, active);

  return (
    <div className="ai-doc-section-view">
      <div className="ai-doc-section-view__content">
        {loading ? (
          <p className="ai-doc-section-view__status">Loading section…</p>
        ) : found && children && children.length > 0 ? (
          <DocContractView content={children} />
        ) : fallbackText ? (
          <pre className="ai-doc-section-view__fallback">{fallbackText}</pre>
        ) : (
          <p className="ai-doc-section-view__status">No preview is available for this section.</p>
        )}
      </div>
      {onViewFull ? (
        <button
          type="button"
          className="ai-doc-section-view__expand"
          disabled={disabled}
          onClick={onViewFull}
        >
          View full document
        </button>
      ) : null}
    </div>
  );
}
